import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Avatar as MuiAvatar } from '@mui/material';
import { Menu, ChevronLeft, Search, Bell } from 'lucide-react';

interface RecruiterHeaderProps {
  user: any;
  activeTab: string;
  setActiveTab: (tab: any) => void;
  setIsMobileSidebarOpen: (open: boolean) => void;
}

/**
 * Thanh Header Trên Cùng Dành Cho Nhà Tuyển Dụng
 * Hiển thị nút menu di động, đường dẫn điều hướng, thanh tìm kiếm nhanh, thông báo và ảnh đại diện người dùng.
 */
export const RecruiterHeader: React.FC<RecruiterHeaderProps> = ({
  user,
  activeTab,
  setActiveTab,
  setIsMobileSidebarOpen,
}) => {
  return (
    <Box
      component="header"
      sx={{
        height: '80px',
        bgcolor: 'white',
        borderBottom: '1px solid #f1f3f5',
        px: 3,
        display: { xs: 'flex', md: 'none' },
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        zIndex: 20,
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
      }}
    >
      {/* Khối bên trái: Nút bật/tắt menu trên thiết bị di động & Tiêu đề trang */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setIsMobileSidebarOpen(true)}
          className="md:hidden p-2 rounded-xl text-gray-500 hover:bg-gray-50 hover:text-[#f97316] transition-colors"
        >
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2">
          <span
            className="text-gray-400 cursor-pointer hover:text-[#f97316] transition-colors text-sm font-medium"
            onClick={() => setActiveTab('overview')}
          >
            Tuyển dụng
          </span>
          <span className="text-gray-300">/</span>
          <span className="font-bold text-gray-800 capitalize text-sm">
            {activeTab === 'settings' && 'Thông tin công ty'}
            {activeTab === 'overview' && 'Quản lý ứng viên'}
            {activeTab === 'candidates' && 'Tìm thêm ứng viên mới'}
            {activeTab === 'saved_candidates' && 'Ứng viên đã lưu'}
            {activeTab === 'postings' && 'Quản lý tin đăng'}
            {activeTab === 'post_job' && 'Đăng tin tuyển dụng mới'}
          </span>
        </div>

        {/* Nút thoát về giao diện chính */}
        <div className="h-4 w-px bg-gray-200 mx-2 hidden sm:block" />
        <Link
          to="/"
          className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-500 hover:text-[#f97316] hover:border-orange-200 hover:bg-orange-50/30 transition-all shadow-sm"
        >
          <ChevronLeft size={14} /> Giao diện chính
        </Link>
      </div>

      {/* Khối bên phải: Tìm kiếm nhanh, Chuông thông báo & Thông tin tài khoản */}
      <div className="flex items-center gap-5">
        {/* Ô Tìm Kiếm Nhanh */}
        <div className="relative hidden sm:block w-64">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Tìm kiếm nhanh..."
            className="w-full h-10 pl-10 pr-4 bg-gray-50 border border-gray-200 rounded-full text-xs font-semibold focus:bg-white focus:border-orange-400 outline-none transition-all"
          />
        </div>

        {/* Chuông Thông Báo */}
        <button className="relative w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 transition-colors">
          <Bell size={20} />
          <span className="absolute top-1 right-1 w-5 h-5 bg-[#f97316] text-white text-[10px] font-black rounded-full flex items-center justify-center border-2 border-white shadow-sm">
            3
          </span>
        </button>

        <div className="w-px h-8 bg-gray-100" />

        {/* Thông Tin Tài Khoản Nhà Tuyển Dụng */}
        <div className="flex items-center gap-3">
          <MuiAvatar
            src={user?.avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150"}
            alt="Recruiter Avatar"
            sx={{ width: 40, height: 40, border: '2px solid #fff0e9', boxShadow: '0 2px 8px rgba(255, 81, 0, 0.08)' }}
          />
          <div className="hidden lg:block text-left">
            <h4 className="font-bold text-xs text-gray-800 leading-none">
              {user?.fullName || 'Nguyễn Văn A'}
            </h4>
            <span className="text-[10px] text-gray-400 font-bold block mt-1 uppercase tracking-wider">
              {user?.role === 'admin' ? 'Admin' : 'Recruiter'}
            </span>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default RecruiterHeader;
